import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeRequests } from "@/hooks/useRealtimeRequests";
import { Radio, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface DashboardStats {
  newOpportunities7d: number;
  delivered7d: number;
  failedDelivery: number;
  openClaims: number;
  blockedProviders: number;
  monthRevenue: number;
}

export default function OperatorDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentOpps, setRecentOpps] = useState<any[]>([]);
  const [openClaims, setOpenClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { recentEvents, isListening } = useRealtimeRequests();

  useEffect(() => {
    loadDashboard();
  }, []);

  // Reload dashboard when matching completes
  useEffect(() => {
    const lastEvent = recentEvents[0];
    if (lastEvent && (lastEvent.status === "matched" || lastEvent.status === "failed")) {
      loadDashboard();
    }
  }, [recentEvents]);

  async function loadDashboard() {
    try {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [oppsRes, claimsRes, providersRes, billingRes] = await Promise.all([
        supabase.from("opportunities").select("id, status, delivery_state, created_at").gte("created_at", sevenDaysAgo),
        supabase.from("opportunity_claims").select("*, provider_profiles(company_name)").in("status", ["submitted", "SUBMITTED", "UNDER_REVIEW"]).order("created_at", { ascending: false }).limit(10),
        supabase.from("provider_profiles").select("id").eq("is_suspended", true),
        supabase.from("opportunity_billing_lines").select("final_cents").eq("month_key", `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`),
      ]);

      const opps = oppsRes.data || [];
      const revenue = (billingRes.data || []).reduce((sum: number, l: any) => sum + (l.final_cents || 0), 0);

      setStats({
        newOpportunities7d: opps.length,
        delivered7d: opps.filter((o: any) => o.delivery_state === "sent" || o.delivery_state === "SENT" || o.delivery_state === "DELIVERED").length,
        failedDelivery: opps.filter((o: any) => o.delivery_state === "failed" || o.delivery_state === "FAILED" || o.delivery_state === "FAILED_DELIVERY").length,
        openClaims: (claimsRes.data || []).length,
        blockedProviders: (providersRes.data || []).length,
        monthRevenue: revenue / 100,
      });

      setOpenClaims(claimsRes.data || []);

      const { data: recent } = await supabase.from("opportunities")
        .select("id, opportunity_number, status, delivery_state, match_score, created_at, requests(category, location)")
        .order("created_at", { ascending: false }).limit(10);
      setRecentOpps(recent || []);
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <OperatorLayout title={t("op.dashTitle")} subtitle={t("op.dashSubtitle")}>
      {loading ? (
        <p className="text-caption">{t("common.loading")}</p>
      ) : (
        <div className="space-y-8">
          {/* Realtime Status Bar */}
          <div className="flex items-center gap-3 px-4 py-3 border border-border bg-card">
            <div className={cn(
              "w-2.5 h-2.5 rounded-full",
              isListening ? "bg-accent animate-pulse" : "bg-muted-foreground"
            )} />
            <span className="text-sm text-muted-foreground">
              {isListening ? t("op.realtimeActive") : t("op.realtimeConnecting")}
            </span>
            <Radio className={cn("w-4 h-4 ml-auto", isListening ? "text-accent" : "text-muted-foreground")} />
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: t("op.kpiNew7d"), value: stats?.newOpportunities7d || 0 },
              { label: t("op.kpiDelivered7d"), value: stats?.delivered7d || 0 },
              { label: t("op.kpiFailed"), value: stats?.failedDelivery || 0, accent: true },
              { label: t("op.kpiClaimsOpen"), value: stats?.openClaims || 0, accent: true },
              { label: t("op.kpiBlocked"), value: stats?.blockedProviders || 0 },
              { label: t("op.kpiRevenue"), value: `${(stats?.monthRevenue || 0).toFixed(0)} €` },
            ].map((kpi) => (
              <div key={kpi.label} className="p-4 border border-border bg-card">
                <p className="text-micro mb-2">{kpi.label}</p>
                <p className={`text-2xl font-medium ${kpi.accent ? "text-accent" : ""}`}>
                  {kpi.value}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Realtime Matching Feed */}
            <div className="border border-border bg-card p-6">
              <h3 className="text-subheadline mb-4">{t("op.liveMatching")}</h3>
              <div className="space-y-2 max-h-[360px] overflow-y-auto">
                {recentEvents.length === 0 ? (
                  <p className="text-caption">{t("op.waitingForRequests")}</p>
                ) : (
                  recentEvents.map((event, i) => (
                    <div
                      key={`${event.id}-${i}`}
                      className={cn(
                        "flex items-start gap-2.5 py-2 px-3 border-b border-border last:border-0 text-sm",
                        i === 0 && "animate-fade-in"
                      )}
                    >
                      {event.status === "matching" && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin mt-0.5 shrink-0" />}
                      {event.status === "matched" && <CheckCircle className="w-4 h-4 text-accent mt-0.5 shrink-0" />}
                      {event.status === "failed" && <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <p className="truncate">{event.message}</p>
                        <p className="text-micro mt-0.5">
                          {event.timestamp.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Recent Opportunities */}
            <div className="border border-border bg-card p-6">
               <h3 className="text-subheadline mb-4">{t("op.recentRequests")}</h3>
              <div className="space-y-3">
                {recentOpps.length === 0 ? (
                  <p className="text-caption">{t("op.noRequests")}</p>
                ) : (
                  recentOpps.map((opp) => (
                    <div key={opp.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-sm font-medium">{opp.opportunity_number}</p>
                        <p className="text-micro">{opp.requests?.category || "—"}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs px-2 py-1 bg-secondary border border-border">
                          {opp.delivery_state || opp.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Open Claims */}
            <div className="border border-border bg-card p-6">
              <h3 className="text-subheadline mb-4">{t("op.openClaims")}</h3>
              <div className="space-y-3">
                {openClaims.length === 0 ? (
                  <p className="text-caption">{t("op.noOpenClaims")}</p>
                ) : (
                  openClaims.map((claim: any) => (
                    <div key={claim.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-sm font-medium">{claim.provider_profiles?.company_name || "—"}</p>
                        <p className="text-micro">{claim.reason_code}</p>
                      </div>
                      <p className="text-micro">{new Date(claim.created_at).toLocaleDateString("de-DE")}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </OperatorLayout>
  );
}
